import { SyncService } from "frontend/services/syncService";
import { useBoardValue } from "frontend/state/board-state";
import { useCanvasElementsObjects, useRawCanvasMetadata } from "frontend/subscriptions";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { useSubscribe } from "replicache-react";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { downloadURI } from "shared/util/utils";
import { stageRefAtom } from "state-atoms/general-atoms";
import { isThumbnailExportModeAtom, posScaleAtom } from "state-atoms/stage-atoms";

export default function CanvasElementsSchemaViewer({
  syncService,
  onClose,
}: {
  syncService: SyncService<RW> | null;
  onClose?: () => void;
}) {
  const { elements, commentElements } = useCanvasElementsObjects(syncService?.getReplicache());
  const metadata = useRawCanvasMetadata(syncService?.getReplicache());
  const [mode, setMode] = useState("elements");
  const [{ board }] = useBoardValue();

  const rawData = useSubscribe(syncService?.getReplicache(), async (tx) => tx.scan().entries().toArray(), null);

  const stageRef = useAtomValue(stageRefAtom);
  const posScale = useAtomValue(posScaleAtom);
  const setThumbnailExportMode = useSetAtom(isThumbnailExportModeAtom);

  let es = {} as any;
  if (mode == "elements") {
    for (const element of elements) {
      const object = element.value as any;
      if (object.hidden?.toString() === "true") {
        continue;
      }
      const id = `${element.type}-${element.id}`;
      es[id] = object;
    }
    for (const comment of commentElements) {
      const object = comment.value;
      if (object.hidden) continue;
      es[comment.type + "-" + comment.id] = comment.value;
    }
  } else if (mode == "metadata") {
    es = metadata;
  } else if (mode == "all") {
    es = rawData;
  } else {
    es = "unhandled mode";
  }

  function copyRegular() {
    const data = JSON.stringify(es, null, 2);
    navigator.clipboard.writeText(data);
  }

  function copyMinified() {
    const data = JSON.stringify(es);
    navigator.clipboard.writeText(data);
  }

  function exportScreenshots() {
    const name = board?.name.toLowerCase().replaceAll(" ", "_");
    (stageRef?.current?.getStage() as any).export(
      "image/png",
      false,
      false,
      posScale,
      posScale.scale,
      name,
      0.9,
      500,
      500
    );
    setThumbnailExportMode(true);
    (stageRef?.current?.getStage() as any).export(
      "image/png",
      false,
      false,
      posScale,
      posScale.scale,
      name + "_thumbnail",
      0.5,
      500,
      500
    );
    setThumbnailExportMode(false);
    const json = JSON.stringify(es);
    const data = "data:text/json;charset=utf-8," + encodeURIComponent(json);
    downloadURI(data, name + ".json");
  }

  function deleteAllElements() {
    if (confirm("Are you sure you want to delete all elements?")) {
      syncService?.mutate.removeMany({
        ids: elements.map((e) => `${e.type}-${e.id}`) as string[],
      });
    }
  }

  return (
    <div
      style={{
        display: "block",
        position: "absolute",
        left: "50%",
        transform: "translate(-50%, 0)",
        bottom: "20px",
        width: "50%",
        height: "300px",
        fontSize: "12px",
        background: "white",
        pointerEvents: "all",
        border: "1px solid black",
        borderRadius: 4,
      }}
    >
      <div
        style={{
          display: "flex",
          flexFlow: "row",
          justifyContent: "stretch",
          backgroundColor: "#f1f1f1",
        }}
      >
        <button
          style={{ flexGrow: 1, border: "none", backgroundColor: mode == "elements" ? "#cccccc" : "inherit" }}
          onClick={() => setMode("elements")}
        >
          Elements
        </button>
        <button
          style={{ flexGrow: 1, border: "none", backgroundColor: mode == "metadata" ? "#cccccc" : "inherit" }}
          onClick={() => setMode("metadata")}
        >
          Metadata
        </button>
        <button
          style={{ flexGrow: 1, border: "none", backgroundColor: mode == "all" ? "#cccccc" : "inherit" }}
          onClick={() => setMode("all")}
        >
          Everything
        </button>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          gap: "10px",
          textDecoration: "underline",
        }}
      >
        <span onClick={copyRegular}>Copy</span>
        <span onClick={copyMinified}>Copy Minified</span>
        <span onClick={exportScreenshots}>Export Screenshot</span>
        <span onClick={deleteAllElements}>Delete all elements</span>
      </div>
      <div>
        <span>Total Count: {Object.keys(es).length}</span>
      </div>
      <div style={{ overflow: "auto", height: "auto", maxHeight: "250px" }}>
        <pre data-testid="devtools" style={{ userSelect: "text" }}>
          {JSON.stringify(es, null, 2)}
        </pre>
      </div>

      <CloseIcon
        onClick={onClose}
        color={"black"}
        style={{
          position: "absolute",
          right: "0px",
          top: "0px",
        }}
      />
    </div>
  );
}
